import { lazy, type ComponentType } from "react";

/**
 * A page loaded on demand, in its own file of the build, so a visitor
 * downloads the code of the page they open rather than of all 440.
 *
 * Unlike React.lazy alone, it renders straight away once loaded: main.tsx
 * loads the open page (preload) before the app first renders, so that render
 * never stops on a fallback and the page's markup, already in the HTML, is
 * replaced by the same page with no blank in between. Pages opened later
 * load as the link is followed (the router keeps the current page on screen
 * meanwhile).
 *
 * The prerender (scripts/build-prerender.cjs) turns these back into plain
 * imports, since it renders without waiting.
 */
export type LazyPage<P> = ComponentType<P> & { preload: () => Promise<void> };

export function lazyPage<P extends object>(load: () => Promise<{ default: ComponentType<P> }>): LazyPage<P> {
  let Loaded: ComponentType<P> | null = null;
  let pending: Promise<void> | null = null;
  const preload = () =>
    (pending ??= load().then(
      (m) => { Loaded = m.default; },
      (err) => { pending = null; throw err; } // a failed download can be tried again
    ));
  const Lazy = lazy(() => preload().then(() => ({ default: Loaded as ComponentType<P> }))) as unknown as ComponentType<P>;
  const Page = (props: P) => (Loaded ? <Loaded {...props} /> : <Lazy {...props} />);
  Page.preload = preload;
  return Page as LazyPage<P>;
}
